import { useEffect, useRef } from "react";
export const useActivityMonitor = (color, toMove, halfmove) => {
    const statsRef = useRef({
        loggedEvents: new Map(),
    });
    const colorRef = useRef(color);
    const toMoveRef = useRef(toMove);
    const halfmoveRef = useRef(halfmove);
    colorRef.current = color;
    toMoveRef.current = toMove;
    halfmoveRef.current = halfmove;
    // Track when it becomes the player's turn to count total moves
    // biome-ignore lint/correctness/useExhaustiveDependencies: Observer
    useEffect(() => {
        if (colorRef.current === toMoveRef.current && halfmoveRef.current > 0) {
            if (!statsRef.current.loggedEvents.has(halfmoveRef.current)) {
                statsRef.current.loggedEvents.set(halfmoveRef.current, false);
            }
        }
    }, [halfmove, toMove]);
    useEffect(() => {
        // Page Visibility
        const handleVisibilityChange = () => {
            if (colorRef.current !== toMoveRef.current)
                return;
            if (halfmoveRef.current > 0) {
                statsRef.current.loggedEvents.set(halfmoveRef.current, true);
            }
        };
        // Window focus/blur
        const handleFocus = () => {
            if (colorRef.current !== toMoveRef.current)
                return;
            if (halfmoveRef.current > 0) {
                statsRef.current.loggedEvents.set(halfmoveRef.current, true);
            }
        };
        const handleBlur = () => {
            if (colorRef.current !== toMoveRef.current)
                return;
            if (halfmoveRef.current > 0) {
                statsRef.current.loggedEvents.set(halfmoveRef.current, true);
            }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("focus", handleFocus);
        window.addEventListener("blur", handleBlur);
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("focus", handleFocus);
            window.removeEventListener("blur", handleBlur);
        };
    }, []);
    const evaluateBrowserActivity = () => {
        const events = statsRef.current.loggedEvents;
        const playerMoves = Array.from(events.entries()).filter(([halfmove]) => halfmove > 0);
        const totalPlayerMoves = playerMoves.length;
        const movesWithEvents = playerMoves.filter(([, hasEvents]) => hasEvents).length;
        if (totalPlayerMoves === 0) {
            return {
                isSuspicious: false,
                movesWithEvents: 0,
                totalMoves: 0,
                consecutiveMovesWithEvents: 0,
                percentageOfMovesWithEvents: 0,
            };
        }
        // Calculate percentage of player's moves with events
        const percentageOfMovesWithEvents = (movesWithEvents / totalPlayerMoves) * 100;
        // Find longest consecutive sequence of player's moves with events
        let maxConsecutive = 0;
        let currentConsecutive = 0;
        const sortedMoves = playerMoves.sort(([a], [b]) => a - b);
        for (const [_halfmove, hasEvents] of sortedMoves) {
            if (hasEvents) {
                currentConsecutive++;
                maxConsecutive = Math.max(maxConsecutive, currentConsecutive);
            }
            else {
                currentConsecutive = 0;
            }
        }
        // Flag as suspicious if:
        // - 3+ consecutive moves with events AND
        // - Events occurred in 30%+ of moves AND
        // - Player made at least 5 moves
        const isSuspicious = maxConsecutive >= 3 &&
            percentageOfMovesWithEvents >= 30 &&
            totalPlayerMoves >= 5;
        return {
            isSuspicious,
            movesWithEvents,
            totalMoves: totalPlayerMoves,
            consecutiveMovesWithEvents: maxConsecutive,
            percentageOfMovesWithEvents: Math.round(percentageOfMovesWithEvents),
        };
    };
    return { statsRef, evaluateBrowserActivity };
};
//# sourceMappingURL=useActivityMonitor.js.map